/**
 * Supabase Design Collaboration Adapter
 *
 * Realtime collaboration adapter for Space Designs using Supabase
 * broadcast and presence primitives.
 *
 * Primary responsibilities:
 * - Maintain a design collaboration room lifecycle
 * - Sync awareness, operations, snapshots, and resync requests
 *
 * Dependencies:
 * - Realtime presence track limiter
 * - Supabase browser client
 * - Design collaboration protocol helpers
 */

import type {
  RealtimeChannel,
  RealtimeChannelSendResponse,
  SupabaseClient
} from '@supabase/supabase-js'

import { getFreshBrowserAccessToken } from '@/lib/auth/fresh-browser-session'
import { enqueuePresenceTrack } from '@/lib/realtime/presence-track-limiter'
import {
  createDesignAwarenessEnvelope,
  createDesignCollaborationStateFromSession,
  createDesignOperationEnvelope,
  createDesignResyncRequestEnvelope,
  createDesignSnapshotEnvelope,
  isDesignBroadcastMessage,
  normalizeDesignAwarenessState
} from '@/lib/spaces/design-collaboration/adapter'
import type {
  DesignAwarenessEnvelope,
  DesignAwarenessState,
  DesignCollaborationAdapter,
  DesignCollaborationParticipant,
  DesignCollaborationSession,
  DesignCollaborationState,
  DesignDocumentAdapter,
  DesignOperationEnvelope,
  DesignResyncRequestEnvelope,
  DesignSnapshotEnvelope,
  DesignParticipantAvailability
} from '@/lib/spaces/design-collaboration/types'
import type { Database } from '@/supabase/types'
import { safeConsole } from '@/lib/utils/safeConsole'

type BrowserSupabase = SupabaseClient<Database>

type RealtimePresencePayload = {
  availability: DesignParticipantAvailability
  color: string
  displayName: string | null
  imageUrl: string | null
  joinedAt: string
  lastSeenAt: string
  role: DesignCollaborationParticipant['role']
  sessionId: string
  userId: string
}

type RemoteAwarenessReceipt = {
  awareness: DesignAwarenessState
  receivedAt: string
}

interface CreateSupabaseDesignCollaborationAdapterOptions {
  getDocumentAdapter?: () => DesignDocumentAdapter | null | undefined
  onOperation?: (operation: DesignOperationEnvelope) => void
  onResyncRequest?: (request: DesignResyncRequestEnvelope) => void
  onSnapshot?: (snapshot: DesignSnapshotEnvelope) => void
  session: DesignCollaborationSession
  supabase: BrowserSupabase
}

const EVENT_NAME_BY_KIND = {
  awareness: 'design:awareness',
  operation: 'design:operation',
  snapshot: 'design:snapshot',
  resyncRequest: 'design:resync-request'
} as const

const PRESENCE_TRACK_DEDUPE_WINDOW_MS = 1_000
const PRESENCE_AVAILABILITY_COALESCE_MS = 300
const REALTIME_AUTH_RETRY_DELAYS_MS = [0, 100, 250, 500] as const
const REALTIME_REJOIN_GRACE_MS = 5_000

// --------------

function isSendResponseFailure(result: RealtimeChannelSendResponse) {
  return result === 'error' || result === 'timed out'
}

// --------------

function sortParticipants(
  participants: DesignCollaborationParticipant[]
) {
  return [...participants].sort((left, right) => {
    if (left.isSelf && !right.isSelf) {
      return -1
    }

    if (!left.isSelf && right.isSelf) {
      return 1
    }

    const leftName = (left.displayName || left.userId).toLowerCase()
    const rightName = (right.displayName || right.userId).toLowerCase()

    return leftName.localeCompare(rightName)
  })
}

// --------------

function toParticipant(
  payload: RealtimePresencePayload,
  selfSessionId: string
): DesignCollaborationParticipant {
  return {
    availability: payload.availability,
    color: payload.color,
    displayName: payload.displayName,
    imageUrl: payload.imageUrl,
    isSelf: payload.sessionId === selfSessionId,
    joinedAt: payload.joinedAt,
    lastSeenAt: payload.lastSeenAt,
    role: payload.role,
    sessionId: payload.sessionId,
    userId: payload.userId
  }
}

// --------------

function toPresencePayload(
  session: DesignCollaborationSession,
  availability: DesignParticipantAvailability,
  joinedAt: string
): RealtimePresencePayload {
  return {
    availability,
    color: session.participant.color,
    displayName: session.participant.displayName,
    imageUrl: session.participant.imageUrl,
    joinedAt,
    lastSeenAt: new Date().toISOString(),
    role: session.participant.role,
    sessionId: session.participant.sessionId,
    userId: session.participant.userId
  }
}

// --------------

function getTimestampMs(value: string) {
  const timestampMs = Date.parse(value)
  return Number.isNaN(timestampMs) ? 0 : timestampMs
}

// --------------

function pickNewestPresenceEntry(
  currentEntry: RealtimePresencePayload | undefined,
  nextEntry: RealtimePresencePayload
) {
  if (!currentEntry) {
    return nextEntry
  }

  return getTimestampMs(nextEntry.lastSeenAt) >=
    getTimestampMs(currentEntry.lastSeenAt)
    ? nextEntry
    : currentEntry
}

// --------------

function waitFor(ms: number) {
  return new Promise<void>(resolve => {
    setTimeout(resolve, ms)
  })
}

// --------------

export function createSupabaseDesignCollaborationAdapter(
  options: CreateSupabaseDesignCollaborationAdapterOptions
): DesignCollaborationAdapter {
  const listeners = new Set<(state: DesignCollaborationState) => void>()
  const initialState = createDesignCollaborationStateFromSession(options.session)

  let currentState = initialState
  let channel: RealtimeChannel | null = null
  let isSubscribed = false
  let joinedAt = options.session.participant.joinedAt
  let localAvailability = options.session.participant.availability
  let localAwareness = normalizeDesignAwarenessState({
    selectedElementIds: []
  })
  let presenceHeartbeat: ReturnType<typeof setInterval> | null = null
  let activePresenceTrackPromise: Promise<void> | null = null
  let shouldQueuePresenceTrack = false
  let queuedPresenceTrackForced = false
  let reconnectFallbackTimer: ReturnType<typeof setTimeout> | null = null
  let staleParticipantPruner: ReturnType<typeof setInterval> | null = null
  let remoteAwarenessBySession: Record<string, RemoteAwarenessReceipt> = {}
  let activeConnectAttempt = 0
  let hasConnectedOnce = false
  let lastTrackedPresenceKey: string | null = null
  let lastTrackedPresenceAt = 0
  let scheduledAvailabilityTrack: ReturnType<typeof setTimeout> | null = null
  let pendingAvailabilityTrackWaiters = new Set<{
    reject: (error: Error) => void
    resolve: () => void
  }>()
  let disconnectPromise: Promise<void> | null = null
  let isDisconnecting = false

  const getDocumentAdapter = () => options.getDocumentAdapter?.() || null

  // --------------

  const getRealtimeAccessToken = async () => {
    for (const delayMs of REALTIME_AUTH_RETRY_DELAYS_MS) {
      if (delayMs > 0) {
        await waitFor(delayMs)
      }

      const accessToken = await getFreshBrowserAccessToken(
        options.supabase,
        {
          onError: (message, error) => safeConsole.warn(message, error)
        }
      )

      if (accessToken) {
        return accessToken
      }
    }

    return null
  }

  // --------------

  const syncRealtimeAuth = async () => {
    const accessToken = await getRealtimeAccessToken()
    if (!accessToken) {
      return false
    }

    await options.supabase.realtime.setAuth(accessToken)
    return true
  }

  // --------------

  const notifyListeners = () => {
    listeners.forEach(listener => {
      listener(currentState)
    })
  }

  // --------------

  const setState = (
    updater:
      | DesignCollaborationState
      | ((state: DesignCollaborationState) => DesignCollaborationState)
  ) => {
    currentState =
      typeof updater === 'function'
        ? updater(currentState)
        : updater

    notifyListeners()
  }

  // --------------

  const clearReconnectFallbackTimer = () => {
    if (!reconnectFallbackTimer) {
      return
    }

    clearTimeout(reconnectFallbackTimer)
    reconnectFallbackTimer = null
  }

  // --------------

  const scheduleReconnectFallback = (
    activeChannel: RealtimeChannel,
    connectAttempt: number,
    message: string
  ) => {
    clearReconnectFallbackTimer()

    reconnectFallbackTimer = setTimeout(() => {
      reconnectFallbackTimer = null

      if (
        channel !== activeChannel ||
        connectAttempt !== activeConnectAttempt ||
        isSubscribed
      ) {
        return
      }

      setState(state => ({
        ...state,
        awarenessBySession: {},
        connectedAt: null,
        lastError: message,
        participants: [],
        status: 'error'
      }))
    }, REALTIME_REJOIN_GRACE_MS)
  }

  // --------------

  const settlePendingAvailabilityTrackWaiters = (error?: Error) => {
    if (pendingAvailabilityTrackWaiters.size === 0) {
      return
    }

    const waiters = Array.from(pendingAvailabilityTrackWaiters)
    pendingAvailabilityTrackWaiters.clear()

    waiters.forEach(waiter => {
      if (error) {
        waiter.reject(error)
        return
      }

      waiter.resolve()
    })
  }

  // --------------

  const clearScheduledAvailabilityTrack = (error?: Error) => {
    if (scheduledAvailabilityTrack) {
      clearTimeout(scheduledAvailabilityTrack)
      scheduledAvailabilityTrack = null
    }

    settlePendingAvailabilityTrackWaiters(error)
  }

  // --------------

  const isFreshTimestamp = (value: string) => {
    const staleAfterMs = options.session.presence.staleAfterMs
    if (!staleAfterMs || staleAfterMs <= 0) {
      return true
    }

    return Date.now() - getTimestampMs(value) <= staleAfterMs
  }

  // --------------

  const buildSelfParticipant = (
    lastSeenAt: string = new Date().toISOString()
  ): DesignCollaborationParticipant => {
    return {
      ...options.session.participant,
      availability: localAvailability,
      joinedAt,
      lastSeenAt
    }
  }

  // --------------

  const buildAwarenessBySession = (activeSessionIds: Set<string>) => {
    const awarenessBySession: Record<string, DesignAwarenessState> = {
      [options.session.participant.sessionId]: localAwareness
    }

    Object.entries(remoteAwarenessBySession).forEach(([sessionId, receipt]) => {
      if (!isFreshTimestamp(receipt.receivedAt)) {
        delete remoteAwarenessBySession[sessionId]
        return
      }

      if (!activeSessionIds.has(sessionId)) {
        return
      }

      awarenessBySession[sessionId] = receipt.awareness
    })

    return awarenessBySession
  }

  // --------------

  const applyLocalPresenceToState = () => {
    const selfParticipant = buildSelfParticipant()

    setState(state => {
      const otherParticipants = state.participants.filter(
        participant =>
          participant.sessionId !== options.session.participant.sessionId
      )

      const participants = sortParticipants([
        selfParticipant,
        ...otherParticipants
      ])

      return {
        ...state,
        awarenessBySession: buildAwarenessBySession(
          new Set(participants.map(participant => participant.sessionId))
        ),
        participants
      }
    })
  }

  // --------------

  const syncPresenceState = () => {
    if (!channel) {
      return
    }

    const presenceState = channel.presenceState<RealtimePresencePayload>()
    const freshestPresenceBySession = new Map<string, RealtimePresencePayload>()

    Object.values(presenceState).forEach(entryList => {
      if (!Array.isArray(entryList) || entryList.length === 0) {
        return
      }

      entryList.forEach(entry => {
        if (!entry || !entry.sessionId) {
          return
        }

        if (!isFreshTimestamp(entry.lastSeenAt)) {
          delete remoteAwarenessBySession[entry.sessionId]
          return
        }

        freshestPresenceBySession.set(
          entry.sessionId,
          pickNewestPresenceEntry(
            freshestPresenceBySession.get(entry.sessionId),
            entry
          )
        )
      })
    })

    const participants = Array.from(freshestPresenceBySession.values()).map(
      entry =>
        toParticipant(
          entry,
          options.session.participant.sessionId
        )
    )

    if (!participants.some(
      participant =>
        participant.sessionId === options.session.participant.sessionId
    )) {
      participants.push(buildSelfParticipant())
    }

    const activeSessionIds = new Set(
      participants.map(participant => participant.sessionId)
    )

    setState(state => ({
      ...state,
      awarenessBySession: buildAwarenessBySession(activeSessionIds),
      participants: sortParticipants(participants)
    }))
  }

  // --------------

  const stopPresenceHeartbeat = () => {
    if (!presenceHeartbeat) {
      return
    }

    clearInterval(presenceHeartbeat)
    presenceHeartbeat = null
  }

  // --------------

  const stopStaleParticipantPruner = () => {
    if (!staleParticipantPruner) {
      return
    }

    clearInterval(staleParticipantPruner)
    staleParticipantPruner = null
  }

  // --------------

  const startPresenceHeartbeat = () => {
    stopPresenceHeartbeat()

    const heartbeatIntervalMs = options.session.presence.heartbeatIntervalMs
    if (!heartbeatIntervalMs || heartbeatIntervalMs <= 0) {
      return
    }

    presenceHeartbeat = setInterval(() => {
      void trackLocalPresence().catch((error) => {
        safeConsole.warn('Design collaboration presence heartbeat failed', error)
      })
    }, heartbeatIntervalMs)
  }

  // --------------

  const startStaleParticipantPruner = () => {
    stopStaleParticipantPruner()

    const staleAfterMs = options.session.presence.staleAfterMs
    if (!staleAfterMs || staleAfterMs <= 0) {
      return
    }

    const pruneIntervalMs = Math.max(
      5_000,
      Math.min(
        options.session.presence.heartbeatIntervalMs || staleAfterMs,
        Math.floor(staleAfterMs / 2)
      )
    )

    staleParticipantPruner = setInterval(() => {
      syncPresenceState()
    }, pruneIntervalMs)
  }

  // --------------

  const getPresenceTrackKey = () => {
    return [
      joinedAt,
      localAvailability,
      options.session.participant.sessionId
    ].join(':')
  }

  // --------------

  const performTrackLocalPresence = async (force: boolean) => {
    if (!channel || !isSubscribed) {
      return
    }

    const now = Date.now()
    const nextPresenceKey = getPresenceTrackKey()

    if (
      !force &&
      nextPresenceKey === lastTrackedPresenceKey &&
      now - lastTrackedPresenceAt < PRESENCE_TRACK_DEDUPE_WINDOW_MS
    ) {
      applyLocalPresenceToState()
      return
    }

    const activeChannel = channel
    const result = await enqueuePresenceTrack(
      activeChannel,
      toPresencePayload(
        options.session,
        localAvailability,
        joinedAt
      ),
      {
        shouldTrack: () => channel === activeChannel && isSubscribed
      }
    )

    if (channel !== activeChannel || !isSubscribed) {
      return
    }

    if (isSendResponseFailure(result)) {
      throw new Error('Failed to track design collaboration presence')
    }

    lastTrackedPresenceKey = nextPresenceKey
    lastTrackedPresenceAt = Date.now()
    applyLocalPresenceToState()
  }

  // --------------

  const trackLocalPresence = async (force = false) => {
    if (activePresenceTrackPromise) {
      shouldQueuePresenceTrack = true
      queuedPresenceTrackForced = queuedPresenceTrackForced || force
      return activePresenceTrackPromise
    }

    activePresenceTrackPromise = (async () => {
      let nextForce = force

      do {
        shouldQueuePresenceTrack = false

        const forceCurrentTrack = nextForce || queuedPresenceTrackForced
        queuedPresenceTrackForced = false

        await performTrackLocalPresence(forceCurrentTrack)
        nextForce = false
      } while (shouldQueuePresenceTrack)
    })().finally(() => {
      activePresenceTrackPromise = null
      shouldQueuePresenceTrack = false
      queuedPresenceTrackForced = false
    })

    return activePresenceTrackPromise
  }

  // --------------

  const scheduleAvailabilityTrack = async () => {
    applyLocalPresenceToState()

    await new Promise<void>((resolve, reject) => {
      pendingAvailabilityTrackWaiters.add({
        reject,
        resolve
      })

      if (scheduledAvailabilityTrack) {
        clearTimeout(scheduledAvailabilityTrack)
      }

      scheduledAvailabilityTrack = setTimeout(() => {
        scheduledAvailabilityTrack = null

        void trackLocalPresence().then(() => {
          settlePendingAvailabilityTrackWaiters()
        }).catch((error) => {
          const normalizedError =
            error instanceof Error
              ? error
              : new Error('Failed to track design collaboration presence')

          settlePendingAvailabilityTrackWaiters(normalizedError)
        })
      }, PRESENCE_AVAILABILITY_COALESCE_MS)
    })
  }

  // --------------

  const broadcastLocalAwareness = async () => {
    if (!channel || !isSubscribed || !options.session.permissions.canBroadcastAwareness) {
      return
    }

    await sendBroadcast('awareness', {
      awareness: createDesignAwarenessEnvelope({
        awareness: localAwareness,
        designId: options.session.designId,
        originSessionId: options.session.participant.sessionId,
        userId: options.session.participant.userId
      }),
      kind: 'awareness',
      protocolVersion: options.session.protocolVersion
    })
  }

  // --------------

  const sendBroadcast = async (
    event: keyof typeof EVENT_NAME_BY_KIND,
    payload: object
  ) => {
    if (!channel || !isSubscribed) {
      throw new Error('Design collaboration channel is not connected')
    }

    const result = await channel.send({
      type: 'broadcast',
      event: EVENT_NAME_BY_KIND[event],
      payload
    })

    if (isSendResponseFailure(result)) {
      throw new Error(`Failed to send ${EVENT_NAME_BY_KIND[event]} event`)
    }
  }

  // --------------

  const removeActiveChannel = async () => {
    if (!channel) {
      return
    }

    const activeChannel = channel
    channel = null
    isSubscribed = false
    isDisconnecting = true

    try {
      await activeChannel.untrack()
    } catch {}

    try {
      await options.supabase.removeChannel(activeChannel)
    } catch {}
  }

  // --------------

  const handleRemoteAwareness = (
    awareness: DesignAwarenessEnvelope
  ) => {
    if (awareness.originSessionId === options.session.participant.sessionId) {
      return
    }

    const receivedAt = new Date().toISOString()
    remoteAwarenessBySession[awareness.originSessionId] = {
      awareness: normalizeDesignAwarenessState(awareness.awareness),
      receivedAt
    }

    if (!currentState.participants.some(
      participant => participant.sessionId === awareness.originSessionId
    )) {
      syncPresenceState()
      return
    }

    setState(state => {
      const participants = sortParticipants(
        state.participants.map(participant => {
          if (participant.sessionId !== awareness.originSessionId) {
            return participant
          }

          return {
            ...participant,
            lastSeenAt: receivedAt
          }
        })
      )

      return {
        ...state,
        awarenessBySession: buildAwarenessBySession(
          new Set(participants.map(participant => participant.sessionId))
        ),
        participants
      }
    })
  }

  // --------------

  const handleRemoteOperation = async (
    operation: DesignOperationEnvelope
  ) => {
    if (operation.originSessionId === options.session.participant.sessionId) {
      return
    }

    try {
      await getDocumentAdapter()?.applyRemoteOperation?.(operation)
    } catch (error) {
      safeConsole.error('Design collaboration operation apply failed', error)
    }

    setState(state => ({
      ...state,
      lastOperation: operation
    }))

    options.onOperation?.(operation)
  }

  // --------------

  const handleRemoteSnapshot = async (
    snapshot: DesignSnapshotEnvelope
  ) => {
    if (snapshot.originSessionId === options.session.participant.sessionId) {
      return
    }

    try {
      await getDocumentAdapter()?.applyRemoteSnapshot?.(snapshot)
    } catch (error) {
      safeConsole.error('Design collaboration snapshot apply failed', error)
    }

    setState(state => ({
      ...state,
      lastSnapshot: snapshot
    }))

    options.onSnapshot?.(snapshot)
  }

  // --------------

  const replyToResyncRequest = async (
    request: DesignResyncRequestEnvelope
  ) => {
    if (request.originSessionId === options.session.participant.sessionId) {
      return
    }

    if (!options.session.permissions.canEdit) {
      return
    }

    const snapshot = await getDocumentAdapter()?.captureSnapshot?.()
    if (snapshot === null || snapshot === undefined) {
      return
    }

    await sendBroadcast('snapshot', {
      kind: 'snapshot',
      protocolVersion: options.session.protocolVersion,
      snapshot: createDesignSnapshotEnvelope({
        designId: options.session.designId,
        originSessionId: options.session.participant.sessionId,
        snapshot,
        userId: options.session.participant.userId,
        version: getDocumentAdapter()?.getVersion?.() ?? null
      })
    })
  }

  // --------------

  const handleRemoteResyncRequest = async (
    request: DesignResyncRequestEnvelope
  ) => {
    if (request.originSessionId === options.session.participant.sessionId) {
      return
    }

    setState(state => ({
      ...state,
      lastResyncRequest: request
    }))

    options.onResyncRequest?.(request)
    await replyToResyncRequest(request)
  }

  // --------------

  const buildChannel = () => {
    const nextChannel = options.supabase.channel(
      options.session.channelName,
      {
        config: {
          broadcast: { self: false },
          presence: {
            key: options.session.presence.key
          },
          private: true
        }
      }
    )

    nextChannel
      .on('presence', { event: 'sync' }, () => {
        syncPresenceState()
      })
      .on('presence', { event: 'join' }, () => {
        syncPresenceState()
      })
      .on('presence', { event: 'leave' }, () => {
        syncPresenceState()
      })
      .on('broadcast', { event: EVENT_NAME_BY_KIND.awareness }, ({ payload }) => {
        if (!isDesignBroadcastMessage(payload) || payload.kind !== 'awareness') {
          return
        }

        handleRemoteAwareness(payload.awareness)
      })
      .on('broadcast', { event: EVENT_NAME_BY_KIND.operation }, ({ payload }) => {
        if (!isDesignBroadcastMessage(payload)) {
          return
        }

        if (payload.kind !== 'operation') {
          return
        }

        void handleRemoteOperation(payload.operation)
      })
      .on('broadcast', { event: EVENT_NAME_BY_KIND.snapshot }, ({ payload }) => {
        if (!isDesignBroadcastMessage(payload)) {
          return
        }

        if (payload.kind !== 'snapshot') {
          return
        }

        void handleRemoteSnapshot(payload.snapshot)
      })
      .on('broadcast', { event: EVENT_NAME_BY_KIND.resyncRequest }, ({ payload }) => {
        if (!isDesignBroadcastMessage(payload)) {
          return
        }

        if (payload.kind !== 'resync-request') {
          return
        }

        void handleRemoteResyncRequest(payload.request)
      })

    return nextChannel
  }

  // --------------

  return {
    async broadcastOperation(operation) {
      if (!options.session.permissions.canBroadcastOperations) {
        throw new Error('You do not have permission to edit this design')
      }

      const envelope = createDesignOperationEnvelope({
        createdAt: operation.createdAt,
        designId: options.session.designId,
        id: operation.id,
        originSessionId: options.session.participant.sessionId,
        payload: operation.payload,
        type: operation.type,
        userId: options.session.participant.userId,
        version: operation.version
      })

      await sendBroadcast('operation', {
        kind: 'operation',
        operation: envelope,
        protocolVersion: options.session.protocolVersion
      })
    },
    async broadcastSnapshot(snapshot, optionsOverrides) {
      if (!options.session.permissions.canBroadcastOperations) {
        throw new Error('You do not have permission to sync this design')
      }

      await sendBroadcast('snapshot', {
        kind: 'snapshot',
        protocolVersion: options.session.protocolVersion,
        snapshot: createDesignSnapshotEnvelope({
          designId: options.session.designId,
          id: optionsOverrides?.id,
          originSessionId: options.session.participant.sessionId,
          snapshot,
          userId: options.session.participant.userId,
          version: optionsOverrides?.version ?? null
        })
      })
    },
    async connect() {
      if (channel && isSubscribed) {
        return
      }

      activeConnectAttempt += 1
      const connectAttempt = activeConnectAttempt
      remoteAwarenessBySession = {}

      setState(state => ({
        ...state,
        connectedAt: null,
        lastError: null,
        status: 'connecting'
      }))

      try {
        const didSyncRealtimeAuth = await syncRealtimeAuth()

        if (!didSyncRealtimeAuth) {
          throw new Error('Design collaboration realtime auth unavailable')
        }
      } catch (error) {
        const message = error instanceof Error
          ? error.message
          : 'Design collaboration realtime auth unavailable'

        safeConsole.warn('Design collaboration realtime auth sync failed', error)
        setState(state => ({
          ...state,
          connectedAt: null,
          lastError: message,
          status: 'error'
        }))
        throw error instanceof Error ? error : new Error(message)
      }

      channel = buildChannel()
      const activeChannel = channel

      await new Promise<void>((resolve, reject) => {
        activeChannel?.subscribe(async (status, error) => {
          if (channel !== activeChannel || connectAttempt !== activeConnectAttempt) {
            return
          }

          if (status === 'SUBSCRIBED') {
            isSubscribed = true
            hasConnectedOnce = true
            joinedAt = new Date().toISOString()
            clearReconnectFallbackTimer()

            try {
              await trackLocalPresence(true)
            } catch (error) {
              safeConsole.warn(
                'Design collaboration initial presence track failed',
                error
              )
              applyLocalPresenceToState()
            }

            syncPresenceState()
            startPresenceHeartbeat()
            startStaleParticipantPruner()
            void broadcastLocalAwareness().catch((error) => {
              safeConsole.warn('Design collaboration awareness broadcast failed', error)
            })

            setState(state => ({
              ...state,
              connectedAt: new Date().toISOString(),
              lastError: null,
              status: 'connected'
            }))

            resolve()

            return
          }

          if (
            status === 'CHANNEL_ERROR' ||
            status === 'TIMED_OUT' ||
            status === 'CLOSED'
          ) {
            if (isDisconnecting || activeChannel !== channel) {
              return
            }

            isSubscribed = false
            stopPresenceHeartbeat()
            stopStaleParticipantPruner()
            remoteAwarenessBySession = {}

            const statusDetail =
              error instanceof Error && error.message.trim()
                ? ` (${error.message.trim()})`
                : ''
            const message = `Design collaboration channel status: ${status}${statusDetail}`
            safeConsole.warn('Design collaboration channel status changed', {
              error,
              status
            })

            if (hasConnectedOnce && status !== 'CLOSED' && activeChannel) {
              scheduleReconnectFallback(activeChannel, connectAttempt, message)

              setState(state => ({
                ...state,
                connectedAt: null,
                lastError: null,
                status: 'connecting'
              }))

              return
            }

            setState(state => ({
              ...state,
              awarenessBySession: {},
              connectedAt: null,
              lastError: message,
              participants: [],
              status: status === 'CLOSED' ? 'closed' : 'error'
            }))

            reject(new Error(message))
          }
        })
      })
    },
    async disconnect() {
      if (disconnectPromise) {
        return disconnectPromise
      }

      disconnectPromise = (async () => {
        isSubscribed = false
        hasConnectedOnce = false
        clearReconnectFallbackTimer()
        activePresenceTrackPromise = null
        shouldQueuePresenceTrack = false
        queuedPresenceTrackForced = false
        lastTrackedPresenceKey = null
        lastTrackedPresenceAt = 0
        activeConnectAttempt += 1
        clearScheduledAvailabilityTrack()
        stopPresenceHeartbeat()
        stopStaleParticipantPruner()
        remoteAwarenessBySession = {}

        await removeActiveChannel()

        setState(state => ({
          ...state,
          awarenessBySession: {},
          connectedAt: null,
          participants: [],
          status: 'closed'
        }))
      })().finally(() => {
        isDisconnecting = false
        disconnectPromise = null
      })

      return disconnectPromise
    },
    getState() {
      return currentState
    },
    async requestResync(reason) {
      const request = createDesignResyncRequestEnvelope({
        designId: options.session.designId,
        knownVersion: getDocumentAdapter()?.getVersion?.() ?? null,
        originSessionId: options.session.participant.sessionId,
        reason,
        userId: options.session.participant.userId
      })

      try {
        await sendBroadcast('resyncRequest', {
          kind: 'resync-request',
          protocolVersion: options.session.protocolVersion,
          request
        })
      } catch (error) {
        safeConsole.warn('Design collaboration resync request failed', error)
      }
    },
    async setAvailability(availability) {
      const previousAvailability = localAvailability
      localAvailability = availability

      if (
        availability === previousAvailability &&
        lastTrackedPresenceKey === getPresenceTrackKey()
      ) {
        applyLocalPresenceToState()
        return
      }

      try {
        await scheduleAvailabilityTrack()
      } catch (error) {
        safeConsole.warn('Design collaboration availability update failed', error)
      }
    },
    subscribe(listener) {
      listeners.add(listener)
      listener(currentState)

      return () => {
        listeners.delete(listener)
      }
    },
    async updateAwareness(patch) {
      localAwareness = normalizeDesignAwarenessState({
        ...localAwareness,
        ...patch
      })

      applyLocalPresenceToState()

      try {
        await broadcastLocalAwareness()
      } catch (error) {
        safeConsole.warn('Design collaboration awareness update failed', error)
      }
    }
  }
}
